import { type PlayerProfileResponse, rdbV1PlayerProfileDetail } from '@on3/api';
import type {
  IGenericPageProps,
  IUser,
} from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { classYearClaimCutoff } from '@on3/ui-lib/utils/constants';
import { getIdFromParam } from '@on3/ui-lib/utils/url';
import { ClaimProfile } from 'components/Db/Profile/ClaimProfile';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { AthleteProvider } from 'contexts/AthleteProvider';
import type { GetServerSideProps } from 'next';
import { createDescription, createTitle } from 'utils/meta';

interface ClaimProfilePageProps extends IGenericPageProps {
  basicInfo: PlayerProfileResponse;
}

const ClaimProfilePage = ({
  basicInfo,
  siteData,
  pageType,
}: ClaimProfilePageProps) => {
  const title = createTitle(basicInfo.name, 'Claim Profile');
  const description = createDescription(
    `Claim {0}'s On3 Player Profile`,
    basicInfo.name,
  );

  return (
    <AthleteProvider data={{ athleteData: basicInfo }}>
      <PageWrapper
        description={description}
        hideAds
        pageType={pageType}
        siteData={siteData}
        title={title}
      >
        <ClaimProfile />
      </PageWrapper>
    </AthleteProvider>
  );
};

export const getServerSideProps: GetServerSideProps<
  ClaimProfilePageProps
> = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { id } = ctx.query as { id: string };
  const personKey = getIdFromParam(id);

  if (!personKey) return { notFound: true };

  const data: ClaimProfilePageProps = {
    pageType: 'recruits',
    siteData,
    userData: userData || ({} as IUser),
    basicInfo: {} as PlayerProfileResponse,
  };

  try {
    const [basicInfoReq] = await Promise.allSettled([
      rdbV1PlayerProfileDetail(proxyApi, { personKey }),
    ]);

    const basicInfo = getFulfilledValue(basicInfoReq);

    if (!basicInfo || basicInfo?.classYear < classYearClaimCutoff)
      return { notFound: true };

    data.basicInfo = basicInfo;

    return {
      props: data,
    };
  } catch (e) {
    return { props: data };
  }
};

export default withValidUrl(ClaimProfilePage);
